// MOD-17 / K-12 — prop şema modeli ⇄ propsSchemaJson round-trip + BE-aynası doğrulama.
//
// propsSchemaJson wire'da bir JSON string'tir: `[{key,type,label,required,options?,default?}]`.
// Tasarımcı `PropFieldModel[]` üzerinde çalışır (dizi sırası = görüntü sırası; client-only
// `id` ve `order` serialize edilmez). Doğrulama BE `PropsSchemaValidator.ValidateForSave` +
// `ComponentTemplate.EnsurePlaceholdersDeclared`'i birebir aynalar → kaydetmeden önce
// 422 (COMPONENT_SCHEMA_INVALID / COMPONENT_TEMPLATE_INVALID) önler ve inline uyarı üretir.

import { COMPONENT_CONSTRAINTS, isPropType, propTypeRequiresOptions, type PropType } from '../constants';
import type { PropFieldModel } from '../types';
import { extractPlaceholders } from './template';

/** Prop key kuralı — BE `IsValidKey` aynası: harf/alt-çizgiyle başlar, harf/rakam/alt-çizgi. */
const KEY_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

function newPropId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Serbest metinden placeholder-güvenli bir prop anahtarı türetir (label → key). */
export function slugifyKey(source: string): string {
  const cleaned = source
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, '_$1'); // rakamla başlayamaz → alt-çizgi öne
  return cleaned.slice(0, COMPONENT_CONSTRAINTS.maxKeyLength);
}

/** Palet'ten yeni prop. `label` verilirse key otomatik türetilir (düzenlenebilir). */
export function createProp(type: PropType, defaults: { label?: string } = {}): PropFieldModel {
  const label = defaults.label ?? '';
  return {
    id: newPropId(),
    key: label ? slugifyKey(label) : '',
    type,
    label,
    required: false,
    options: [],
    default: '',
  };
}

function toDefaultString(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return '';
}

/** Depolanan (geçerli) şemayı tasarımcı modeline çözer (BE `Parse` aynası — hoşgörülü). */
export function parsePropsSchema(propsSchemaJson: string | null | undefined): PropFieldModel[] {
  if (!propsSchemaJson || !propsSchemaJson.trim()) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(propsSchemaJson);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  const out: PropFieldModel[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const r = item as Record<string, unknown>;
    const key = typeof r.key === 'string' ? r.key.trim() : '';
    const rawType = typeof r.type === 'string' ? r.type.trim().toLowerCase() : '';
    if (!key || !isPropType(rawType)) continue;
    const type = rawType;
    out.push({
      id: newPropId(),
      key,
      type,
      label: typeof r.label === 'string' ? r.label : '',
      required: r.required === true,
      options:
        propTypeRequiresOptions(type) && Array.isArray(r.options)
          ? r.options.map((o) => String(o))
          : [],
      default: toDefaultString(r.default),
    });
  }
  return out;
}

/** Tasarımcı modelini depolama için canonical JSON string'e çevirir (dizi sırası korunur). */
export function serializePropsSchema(fields: PropFieldModel[]): string {
  const out = fields.map((f) => {
    const item: Record<string, unknown> = {
      key: f.key.trim(),
      type: f.type,
      label: f.label.trim(),
      required: f.required,
    };
    if (propTypeRequiresOptions(f.type)) {
      item.options = f.options.map((o) => o.trim()).filter((o) => o.length > 0);
    }
    const def = f.default.trim();
    if (def.length > 0) {
      // default JSON tipini prop tipine göre koru (BE değeri olduğu gibi DeepClone eder).
      if (f.type === 'boolean') item.default = def.toLowerCase() === 'true';
      else if (f.type === 'number' && Number.isFinite(Number(def))) item.default = Number(def);
      else item.default = def;
    }
    return item;
  });
  return JSON.stringify(out);
}

export interface SchemaValidationError {
  /** null = form seviyesi (alan-bağımsız) ya da şablon hatası. */
  fieldId: string | null;
  /** i18n suffix (component-studio:builder.errors.<code>). */
  code: string;
  /** İnterpolasyon parametresi (key / placeholder). */
  value?: string;
}

/** BE `PropsSchemaValidator.ValidateForSave` client aynası. */
export function validatePropsSchema(fields: PropFieldModel[]): SchemaValidationError[] {
  const errors: SchemaValidationError[] = [];
  const C = COMPONENT_CONSTRAINTS;
  if (fields.length > C.maxFields) errors.push({ fieldId: null, code: 'tooManyFields' });

  const seen = new Set<string>();
  for (const f of fields) {
    const key = f.key.trim();
    if (key.length === 0) {
      errors.push({ fieldId: f.id, code: 'keyRequired' });
    } else if (key.length > C.maxKeyLength) {
      errors.push({ fieldId: f.id, code: 'keyTooLong' });
    } else if (!KEY_REGEX.test(key)) {
      errors.push({ fieldId: f.id, code: 'keyInvalid', value: key });
    } else {
      const lower = key.toLowerCase();
      if (seen.has(lower)) errors.push({ fieldId: f.id, code: 'keyDuplicate', value: key });
      seen.add(lower);
    }

    const label = f.label.trim();
    if (label.length === 0) errors.push({ fieldId: f.id, code: 'labelRequired' });
    else if (label.length > C.maxLabelLength) errors.push({ fieldId: f.id, code: 'labelTooLong' });

    if (propTypeRequiresOptions(f.type)) {
      const opts = f.options.map((o) => o.trim()).filter((o) => o.length > 0);
      if (opts.length === 0) errors.push({ fieldId: f.id, code: 'optionsRequired' });
      else if (opts.length > C.maxOptionsPerField) errors.push({ fieldId: f.id, code: 'tooManyOptions' });
      if (opts.some((o) => o.length > C.maxOptionLength)) {
        errors.push({ fieldId: f.id, code: 'optionTooLong' });
      }
    }
  }
  return errors;
}

/** BE `ComponentTemplate.EnsurePlaceholdersDeclared` + uzunluk sınırı aynası. */
export function validateTemplate(
  templateHtml: string,
  fields: PropFieldModel[],
): SchemaValidationError[] {
  const errors: SchemaValidationError[] = [];
  if (templateHtml.length > COMPONENT_CONSTRAINTS.maxTemplateLength) {
    errors.push({ fieldId: null, code: 'templateTooLong' });
  }
  const declared = new Set(fields.map((f) => f.key.trim()).filter(Boolean));
  for (const placeholder of extractPlaceholders(templateHtml)) {
    if (!declared.has(placeholder)) {
      errors.push({ fieldId: null, code: 'placeholderUndeclared', value: placeholder });
    }
  }
  return errors;
}

/** Ad + kategori + şema + şablon → tüm kaydetme-öncesi doğrulamalar. */
export function validateComponent(input: {
  name: string;
  category: string;
  fields: PropFieldModel[];
  templateHtml: string;
}): SchemaValidationError[] {
  const errors: SchemaValidationError[] = [];
  const name = input.name.trim();
  if (name.length === 0) errors.push({ fieldId: null, code: 'nameRequired' });
  else if (name.length > COMPONENT_CONSTRAINTS.maxNameLength) {
    errors.push({ fieldId: null, code: 'nameTooLong' });
  }
  if (input.category.trim().length > COMPONENT_CONSTRAINTS.maxCategoryLength) {
    errors.push({ fieldId: null, code: 'categoryTooLong' });
  }
  return [
    ...errors,
    ...validatePropsSchema(input.fields),
    ...validateTemplate(input.templateHtml, input.fields),
  ];
}
